import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * Keep storefront search behavior consistent regardless of how customers
 * capitalize their query.
 * @param {string} value
 * @returns {string}
 */
const normalizeSearchQuery = (value) => value.trim().toLocaleLowerCase();

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    this.closest('form')?.addEventListener('submit', this.#normalizeSubmittedQuery, { signal });

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (this.dataset.inline === 'true') {
      this.addEventListener('focusin', this.#openInlineResults, { signal });
      this.addEventListener('click', this.#openInlineResults, { signal });
      document.addEventListener('pointerdown', this.#handleInlineOutside, { signal });
    }

    if (dialog && RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  #normalizeSubmittedQuery = () => {
    this.refs.searchInput.value = normalizeSearchQuery(this.refs.searchInput.value);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.search.cancel();
    this.#activeFetch?.abort();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #openInlineResults = (event) => {
    if (this.dataset.inline !== 'true') return;
    if (event?.target instanceof Element && event.target.closest('[data-predictive-search-dismiss]')) return;
    const { predictiveSearchResults, searchInput } = this.refs;
    if (!normalizeSearchQuery(searchInput.value)) {
      this.#closeInlineResults();
      return;
    }
    predictiveSearchResults.hidden = false;
    searchInput.setAttribute('aria-expanded', 'true');
  };

  #closeInlineResults = () => {
    if (this.dataset.inline !== 'true') return;
    const { predictiveSearchResults, searchInput } = this.refs;
    predictiveSearchResults.hidden = true;
    searchInput.setAttribute('aria-expanded', 'false');
    this.#currentIndex = -1;
  };

  dismissInlineResults = () => {
    if (this.dataset.inline !== 'true') return;
    this.search.cancel();
    this.#activeFetch?.abort();
    this.refs.searchInput.value = '';
    this.refs.predictiveSearchResults.removeAttribute('aria-busy');
    this.#hideResetButton();
    this.#setInlineStatus('');
    this.#closeInlineResults();
    this.refs.predictiveSearchResults.replaceChildren();
    this.refs.searchInput.removeAttribute('aria-activedescendant');
  };

  #handleInlineOutside = (event) => {
    if (!this.contains(event.target)) this.#closeInlineResults();
  };

  #setInlineStatus(message) {
    if (this.dataset.inline !== 'true') return;
    if (message) this.refs.predictiveSearchResults.dataset.searchStatus = message;
    else delete this.refs.predictiveSearchResults.dataset.searchStatus;
    const status = this.querySelector('[ref="liveStatus"]');
    if (status) status.textContent = message;
  }

  /**
   * Applies combobox/listbox semantics after section markup is morphed.
   */
  #prepareResultSemantics() {
    const { predictiveSearchResults, searchInput } = this.refs;
    const inputId = searchInput.id || 'predictive-search-input';

    if (this.dataset.inline === 'true') {
      predictiveSearchResults.setAttribute('role', 'listbox');
    } else {
      predictiveSearchResults.removeAttribute('role');
    }
    this.#allResultsItems.forEach((item, index) => {
      item.id = `${inputId}-option-${index}`;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
    });
    searchInput.removeAttribute('aria-activedescendant');
  }

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.setAttribute('aria-selected', 'false');
      }
    }

    if (activeItem?.id) {
      this.refs.searchInput.setAttribute('aria-activedescendant', activeItem.id);
    } else {
      this.refs.searchInput.removeAttribute('aria-activedescendant');
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.dataset.inline === 'true') this.dismissInlineResults();
      else this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType && this.dataset.inline !== 'true') return;

    const searchTerm = normalizeSearchQuery(this.refs.searchInput.value);
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#setInlineStatus('Loading search results');
    this.#openInlineResults();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Commit fresh inline boxes on mobile WebKit, including equal-height updates.
   * Inline result CSS disables reveal animations, so rebuilding these boxes cannot
   * restart an opacity-zero reveal. Preserve the owner's exact inline display.
   * @param {HTMLElement} results
   */
  #refreshInlineResultsLayout(results) {
    if (!window.matchMedia('(max-width: 1024px)').matches || !results.isConnected || results.hidden) return;
    const display = results.style.getPropertyValue('display');
    const priority = results.style.getPropertyPriority('display');
    results.style.setProperty('display', 'none', 'important');
    void results.offsetHeight;
    if (display) results.style.setProperty('display', display, priority);
    else results.style.removeProperty('display');
    void results.offsetHeight;
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();
    predictiveSearchResults.setAttribute('aria-busy', 'true');

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    this.#fetchSearchMarkup(url, searchTerm, abortController.signal)
      .then((resultsMarkup) => {
        if (!resultsMarkup) throw new Error('Search returned an empty response');

        if (abortController.signal.aborted || this.#activeFetch !== abortController) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (normalizeSearchQuery(this.refs.searchInput.value) !== searchTerm) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        const resultsId = predictiveSearchResults.id || this.refs.searchInput.getAttribute('aria-controls');
        const incomingResults = new DOMParser()
          .parseFromString(resultsMarkup, 'text/html')
          .getElementById(resultsId);

        if (!incomingResults) {
          throw new Error(`Predictive search response is missing #${resultsId}`);
        }

        if (this.dataset.inline === 'true') {
          const products = incomingResults.querySelector('#predictive-search-products');
          if (products) products.id = 'hn-header-search-products';
        }

        const morphTarget =
          predictiveSearchResults.id === resultsId
            ? predictiveSearchResults
            : predictiveSearchResults.querySelector(`#${CSS.escape(resultsId)}`);

        if (!morphTarget) {
          throw new Error(`Predictive search owner is missing #${resultsId}`);
        }

        if (this.dataset.inline === 'true') {
          // Fresh connected nodes avoid retaining animation state from a previous query.
          // The owner and input remain stable; custom elements reconnect normally.
          morphTarget.replaceChildren(...Array.from(incomingResults.childNodes, (node) => document.importNode(node, true)));
          this.#refreshInlineResultsLayout(morphTarget);
        } else {
          morph(morphTarget, incomingResults);
        }
        if (!predictiveSearchResults.id && !predictiveSearchResults.querySelector('#' + CSS.escape(resultsId))) {
          predictiveSearchResults.id = resultsId;
        }
        this.#prepareResultSemantics();
        predictiveSearchResults.removeAttribute('aria-busy');
        if (this.dataset.inline !== 'true') this.refs.searchInput.setAttribute('aria-expanded', 'true');
        this.#openInlineResults();
        this.#setInlineStatus('');

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = this.#allResultsItems.length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        if (abortController.signal.aborted || error?.name === 'AbortError' || this.#activeFetch !== abortController ||
          normalizeSearchQuery(this.refs.searchInput.value) !== searchTerm) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }
        if (this.#activeFetch === abortController) predictiveSearchResults.removeAttribute('aria-busy');
        deferredPromise.resolve({ totalCount: 0 });
        this.#setInlineStatus('Search results are temporarily unavailable');
        this.#openInlineResults();
        console.error(error);
      });
  }

  /** Fetch inline results with one prefix fallback when predictive search returns no items. */
  async #fetchSearchMarkup(url, searchTerm, signal) {
    if (this.dataset.inline !== 'true') {
      return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url, signal);
    }
    const fetchSection = async (requestUrl) => {
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(requestUrl, { signal });
      if (!response.ok) throw new Error(`Search request failed (${response.status})`);
      return response.text();
    };
    const markup = await fetchSection(url);
    const resultsId = this.refs.predictiveSearchResults.id;
    const results = new DOMParser().parseFromString(markup, 'text/html').getElementById(resultsId);
    // Only plain text queries qualify. Never reinterpret Shopify search operators.
    if (!results || results.querySelector('[ref="resultsItems[]"]:not([data-search-query-action])') ||
      !/^[\p{L}\p{N}\s'-]+$/u.test(searchTerm) || !/[\p{L}\p{N}]{2}$/u.test(searchTerm) ||
      signal.aborted || normalizeSearchQuery(this.refs.searchInput.value) !== searchTerm) return markup;
    const fallbackUrl = new URL(Theme.routes.search_url, location.origin);
    fallbackUrl.searchParams.set('q', `${searchTerm}*`);
    fallbackUrl.searchParams.set('type', 'product');
    fallbackUrl.searchParams.set('options[prefix]', 'last');
    const fallbackMarkup = await fetchSection(fallbackUrl);
    const fallbackResults = new DOMParser().parseFromString(fallbackMarkup, 'text/html').getElementById(resultsId);
    if (!fallbackResults) throw new Error(`Search fallback is missing #${resultsId}`);
    // Shopify can typo-correct a prefix into unrelated products. Only retain title-prefix matches.
    const words = searchTerm.match(/[\p{L}\p{N}]+/gu) || [];
    for (const card of fallbackResults.querySelectorAll('.predictive-search-results__card--product')) {
      const titleWords = normalizeSearchQuery(card.querySelector('.resource-card__title')?.textContent || '')
        .match(/[\p{L}\p{N}]+/gu) || [];
      if (!words.every((word) => titleWords.some((titleWord) => titleWord.startsWith(word)))) card.remove();
      else card.setAttribute('ref', 'resultsItems[]');
    }
    return fallbackResults.querySelector('[ref="resultsItems[]"]:not([data-search-query-action])') ? fallbackResults.outerHTML : markup;
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    this.search.cancel();
    predictiveSearchResults.removeAttribute('aria-busy');
    this.#setInlineStatus('');
    searchInput.value = '';
    this.#hideResetButton();
    if (this.dataset.inline !== 'true') this.refs.searchInput.setAttribute('aria-expanded', 'false');

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    let emptySectionMarkup;
    try {
      emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    } catch (error) {
      if (abortController.signal.aborted || error?.name === 'AbortError') return;
      throw error;
    }

    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      let recentlyViewedMarkup;
      try {
        recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      } catch (error) {
        if (abortController.signal.aborted || error?.name === 'AbortError') return;
        throw error;
      }
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#prepareResultSemantics();
    this.#setInlineStatus('');
    this.#closeInlineResults();
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
